import { useState, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Settings, X } from "lucide-react";
import { Button } from "./ui/button";

const CONSENT_KEY = "futura_cookie_consent";
const CONSENT_VERSION = "1.0";

export const getCookieConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const consent = getCookieConsent();
  return consent?.analytics === true;
};

const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    }

    const handleOpenSettings = () => {
      setVisible(true);
      setShowConfig(true);
      const consent = getCookieConsent();
      if (consent) setAnalytics(consent.analytics || false);
    };
    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  const saveConsent = (analyticsValue) => {
    const consent = {
      version: CONSENT_VERSION,
      essential: true,
      analytics: analyticsValue,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
    setShowConfig(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] p-4 sm:p-6"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 16px)" }}
      data-testid="cookie-banner"
    >
      <div className="max-w-lg mx-auto bg-[#1a1a2e] border border-white/10 rounded-2xl p-5 shadow-2xl">
        {!showConfig ? (
          <>
            <div className="flex items-start justify-between gap-3 mb-3">
              <p className="text-white text-sm font-medium">{t("cookie_title")}</p>
            </div>
            <p className="text-white/60 text-xs leading-relaxed mb-4">
              {t("cookie_description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => saveConsent(true)}
                className="flex-1 bg-[#002FA7] hover:bg-[#001d6a] active:bg-[#001050] text-white rounded-xl text-sm min-h-[44px]"
                data-testid="cookie-accept"
              >
                {t("cookie_accept")}
              </Button>
              <Button
                onClick={() => saveConsent(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/5 active:bg-white/10 rounded-xl text-sm min-h-[44px]"
                data-testid="cookie-reject"
              >
                {t("cookie_reject")}
              </Button>
              <Button
                onClick={() => setShowConfig(true)}
                variant="ghost"
                className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl text-sm min-h-[44px]"
                data-testid="cookie-configure"
              >
                <Settings className="w-4 h-4 mr-1.5" />
                {t("cookie_configure")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-white text-sm font-medium">{t("cookie_config_title")}</p>
              <button onClick={() => { setShowConfig(false); if (!getCookieConsent()) setVisible(true); else setVisible(false); }} className="text-white/40 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-xl">
                <div>
                  <p className="text-white text-xs font-medium">{t("cookie_essential")}</p>
                  <p className="text-white/40 text-[10px]">{t("cookie_essential_desc")}</p>
                </div>
                <span className="text-[10px] text-white/30 bg-white/10 px-2 py-0.5 rounded-full">{t("cookie_always_active")}</span>
              </div>

              <div className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-xl">
                <div>
                  <p className="text-white text-xs font-medium">{t("cookie_analytics")}</p>
                  <p className="text-white/40 text-[10px]">{t("cookie_analytics_desc")}</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`w-10 h-6 rounded-full transition-colors relative ${analytics ? "bg-[#002FA7]" : "bg-white/20"}`}
                  data-testid="cookie-analytics-toggle"
                >
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${analytics ? "translate-x-5" : "translate-x-1"}`} />
                </button>
              </div>
            </div>

            <Button
              onClick={() => saveConsent(analytics)}
              className="w-full bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl text-sm min-h-[44px]"
              data-testid="cookie-save-preferences"
            >
              {t("cookie_save")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
